import { useQuery } from "@tanstack/react-query";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/shared/ui/sheet";
import { Badge } from "@/shared/ui/badge";
import { ScrollArea } from "@/shared/ui/scroll-area";
import { Separator } from "@/shared/ui/separator";
import { Skeleton } from "@/shared/ui/skeleton";
import { CheckCircle2, XCircle, MinusCircle, User, CalendarDays, FileText, Clock, Activity } from "lucide-react";
import { format } from "date-fns";
import { fetchRunWithResponses } from "@/modules/checklist/api";
import { useLanguage } from "@/core/LanguageContext";
import type { ChecklistRunResponseWithItem } from "@/modules/checklist/api/types";

interface RunDetailSheetProps {
  tenantId: string;
  runId: string | null;
  open: boolean;
  onClose: () => void;
}

function StatusBadge({ status }: { status: string }) {
  if (status === "completed") {
    return <Badge variant="default" className="gap-1 bg-green-600 hover:bg-green-600"><CheckCircle2 className="w-3 h-3" />Completed</Badge>;
  }
  if (status === "failed") {
    return <Badge variant="destructive" className="gap-1"><XCircle className="w-3 h-3" />Failed</Badge>;
  }
  return <Badge variant="secondary" className="gap-1"><Clock className="w-3 h-3" />In Progress</Badge>;
}

function ResponseValueDisplay({ response }: { response: ChecklistRunResponseWithItem }) {
  const itemType = response.item?.item_type ?? "text";

  if (itemType === "yes_no") {
    if (response.response_value === "yes" || response.is_passed === true) {
      return (
        <span className="inline-flex items-center gap-1 text-green-600 font-medium text-sm">
          <CheckCircle2 className="w-3.5 h-3.5" />
          Pass
        </span>
      );
    }
    if (response.response_value === "no" || response.is_passed === false) {
      return (
        <span className="inline-flex items-center gap-1 text-destructive font-medium text-sm">
          <XCircle className="w-3.5 h-3.5" />
          Fail
        </span>
      );
    }
  }

  // Field lookup: show snapshots if available, otherwise try structured payload
  if (itemType === "field_lookup") {
    const snapshots = response.field_snapshots ?? [];
    const payload = response.response_payload as Record<string, unknown> | null;
    const payloadFieldValues = payload?.type === "field_lookup"
      ? (payload.field_values as Array<{ display_name: string; value: string; unit: string | null }> | undefined)
      : undefined;

    // Prefer snapshots (authoritative), fallback to payload
    if (snapshots.length > 0) {
      return (
        <div className="space-y-1.5">
          {snapshots.map((snap) => {
            const meta = snap.metadata as Record<string, unknown> | null;
            const displayName = (meta?.display_name as string) || "Field";
            const unit = (meta?.unit as string) || null;
            const isFailed = snap.capture_status !== "ok";
            return (
              <div key={snap.id} className="flex items-center gap-2">
                <Activity className="w-3.5 h-3.5 text-primary shrink-0" />
                <span className="text-xs text-muted-foreground">{displayName}:</span>
                <span className={`text-sm font-medium ${isFailed ? "text-muted-foreground italic" : ""}`}>
                  {snap.captured_value ?? (isFailed ? snap.capture_status : "N/A")}
                </span>
                {unit && snap.captured_value && (
                  <span className="text-xs text-muted-foreground">{unit}</span>
                )}
              </div>
            );
          })}
        </div>
      );
    }

    if (payloadFieldValues && payloadFieldValues.length > 0) {
      return (
        <div className="space-y-1.5">
          {payloadFieldValues.map((fv, i) => (
            <div key={i} className="flex items-center gap-2">
              <Activity className="w-3.5 h-3.5 text-primary shrink-0" />
              <span className="text-xs text-muted-foreground">{fv.display_name}:</span>
              <span className="text-sm font-medium">{fv.value || "N/A"}</span>
              {fv.unit && fv.value && (
                <span className="text-xs text-muted-foreground">{fv.unit}</span>
              )}
            </div>
          ))}
        </div>
      );
    }

    // Fallback to plain response_value (legacy single-mapping)
    if (response.response_value) {
      return (
        <div className="flex items-center gap-2">
          <Activity className="w-3.5 h-3.5 text-primary shrink-0" />
          <span className="text-sm font-medium">{response.response_value}</span>
        </div>
      );
    }
  }

  if (!response.response_value) {
    return <span className="text-muted-foreground text-sm italic">No response</span>;
  }

  return <span className="text-sm font-medium">{response.response_value}</span>;
}

export function RunDetailSheet({ tenantId, runId, open, onClose }: RunDetailSheetProps) {
  const { getTranslation } = useLanguage();

  const { data: run, isLoading } = useQuery({
    queryKey: ["core", "checklist-run-detail", tenantId, runId],
    queryFn: () => fetchRunWithResponses(tenantId, runId!),
    enabled: !!tenantId && !!runId && open,
  });

  const passCount = run?.responses.filter((r) => r.is_passed === true).length ?? 0;
  const failCount = run?.responses.filter((r) => r.is_passed === false).length ?? 0;
  const totalCount = run?.responses.length ?? 0;

  const operatorDisplay = run?.operator
    ? run.operator.full_name || run.operator.email
    : "Unknown operator";

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl flex flex-col gap-0 p-0">
        <SheetHeader className="p-6 pb-4 border-b border-border">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <SheetTitle className="text-lg leading-tight">
                {isLoading ? (
                  <Skeleton className="h-6 w-48" />
                ) : (
                  run?.template?.name ?? "Work Instruction Run"
                )}
              </SheetTitle>
              <SheetDescription className="mt-1">
                Completion detail
              </SheetDescription>
            </div>
            {run && <StatusBadge status={run.status} />}
          </div>
        </SheetHeader>

        <ScrollArea className="flex-1">
          <div className="p-6 space-y-6">
            {isLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            ) : run ? (
              <>
                {/* Meta info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-start gap-2">
                    <User className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-muted-foreground">Operator</p>
                      <p className="text-sm font-medium">{operatorDisplay}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <CalendarDays className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-muted-foreground">Shift date</p>
                      <p className="text-sm font-medium">
                        {format(new Date(run.shift_date), "MMM d, yyyy")}
                      </p>
                    </div>
                  </div>
                  {run.completed_at && (
                    <div className="flex items-start gap-2">
                      <Clock className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-muted-foreground">Completed at</p>
                        <p className="text-sm font-medium">
                          {format(new Date(run.completed_at), "MMM d, yyyy HH:mm")}
                        </p>
                      </div>
                    </div>
                  )}
                  {run.notes && (
                    <div className="col-span-2 flex items-start gap-2">
                      <FileText className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-muted-foreground">Notes</p>
                        <p className="text-sm">{run.notes}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Summary counts */}
                {totalCount > 0 && (
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border border-border">
                    <div className="flex-1 text-center">
                      <p className="text-2xl font-bold">{totalCount}</p>
                      <p className="text-xs text-muted-foreground">Total items</p>
                    </div>
                    <Separator orientation="vertical" className="h-10" />
                    <div className="flex-1 text-center">
                      <p className="text-2xl font-bold text-green-600">{passCount}</p>
                      <p className="text-xs text-muted-foreground">Passed</p>
                    </div>
                    <Separator orientation="vertical" className="h-10" />
                    <div className="flex-1 text-center">
                      <p className="text-2xl font-bold text-destructive">{failCount}</p>
                      <p className="text-xs text-muted-foreground">Failed</p>
                    </div>
                    <Separator orientation="vertical" className="h-10" />
                    <div className="flex-1 text-center">
                      <p className="text-2xl font-bold text-muted-foreground">
                        {totalCount - passCount - failCount}
                      </p>
                      <p className="text-xs text-muted-foreground">Other</p>
                    </div>
                  </div>
                )}

                <Separator />

                {/* Response list */}
                {run.responses.length === 0 ? (
                  <div className="py-8 text-center text-muted-foreground">
                    <MinusCircle className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    <p className="text-sm">No responses recorded</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                      Responses ({run.responses.length})
                    </h3>
                    <div className="space-y-2">
                      {run.responses.map((response, index) => {
                        const label = response.item
                          ? getTranslation(response.item.label_translations as Record<string, string> | null, response.item.label)
                          : "Unknown item";

                        let passIndicator: React.ReactNode = null;
                        if (response.is_passed === true) {
                          passIndicator = <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />;
                        } else if (response.is_passed === false) {
                          passIndicator = <XCircle className="w-4 h-4 text-destructive flex-shrink-0" />;
                        } else {
                          passIndicator = <MinusCircle className="w-4 h-4 text-muted-foreground/40 flex-shrink-0" />;
                        }

                        return (
                          <div
                            key={response.id}
                            className="flex items-start gap-3 p-3 rounded-lg border border-border bg-card"
                          >
                            {passIndicator}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-foreground leading-snug">
                                <span className="text-muted-foreground mr-1.5">{index + 1}.</span>
                                {label}
                              </p>
                              <div className="mt-1">
                                <ResponseValueDisplay response={response} />
                              </div>
                              {response.photo_url && (
                                <a
                                  href={response.photo_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="mt-1.5 inline-flex items-center gap-1 text-xs text-primary underline"
                                >
                                  View photo
                                </a>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="py-8 text-center text-muted-foreground">
                <p className="text-sm">Run not found</p>
              </div>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
